/**
 * YouTube Data API v3 client for the lesson-plan video search feature.
 *
 * The query comes from the AI-generated `media_search_query` on a slide (or a
 * user-typed override). We do not use `topicId`: YouTube only supports a small
 * curated set (music, gaming, etc.) that does not map well to session-specific
 * mental-performance clips; a contextual `q` string is the right lever.
 *
 * `safeSearch` is always `strict` per search.list guidance for youth-facing content.
 *
 * NOTE: REACT_APP_YOUTUBE_SEARCH_KEY ships in the client bundle. Restrict it to the
 * YouTube Data API and to HTTP referrers in Google Cloud. The in-memory cache below
 * limits repeat calls against the daily quota (search.list costs 100 units).
 */

/** Number of results shown in the picker grid (each search.list call still costs 100 units). */
export const YOUTUBE_SEARCH_RESULT_COUNT = 6;

export interface YouTubeVideo {
    videoId: string;
    title: string;
    channelTitle: string;
    thumbnail: string;
    publishedAt: string;
}

export interface YouTubeSearchPage {
    videos: YouTubeVideo[];
    nextPageToken?: string;
    prevPageToken?: string;
}

interface SearchOptions {
    maxResults?: number;
    pageToken?: string;
    signal?: AbortSignal;
}

const SEARCH_ENDPOINT = "https://www.googleapis.com/youtube/v3/search";

// In-memory cache keyed by query + page for the session, to limit quota usage.
const resultCache = new Map<string, YouTubeSearchPage>();

/** Embed URL using the privacy-enhanced nocookie domain (no autoplay). */
export function embedUrl(videoId: string): string {
    const params = new URLSearchParams({
        autoplay: "0",
        rel: "0",
        modestbranding: "1",
    });
    return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`;
}

/** Public watch URL, used as a fallback when a video cannot be embedded. */
export function watchUrl(videoId: string): string {
    return `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`;
}

export async function searchYouTube(
    query: string,
    { maxResults = YOUTUBE_SEARCH_RESULT_COUNT, pageToken, signal }: SearchOptions = {}
): Promise<YouTubeSearchPage> {
    const trimmed = (query ?? "").trim();
    if (!trimmed) {
        return { videos: [] };
    }

    const apiKey = process.env.REACT_APP_YOUTUBE_SEARCH_KEY;
    if (!apiKey) {
        throw new Error("YouTube search is not configured (missing REACT_APP_YOUTUBE_SEARCH_KEY).");
    }

    const cacheKey = `${maxResults}:${trimmed}:${pageToken ?? ""}`;
    const cached = resultCache.get(cacheKey);
    if (cached) {
        return cached;
    }

    const params = new URLSearchParams({
        part: "snippet",
        type: "video",
        videoEmbeddable: "true",
        safeSearch: "strict",
        order: "relevance",
        videoDuration: "medium",
        maxResults: String(maxResults),
        q: trimmed,
        key: apiKey,
    });
    if (pageToken) {
        params.set("pageToken", pageToken);
    }

    const response = await fetch(`${SEARCH_ENDPOINT}?${params.toString()}`, {
        signal,
        credentials: "omit",
    });

    if (!response.ok) {
        let message = `YouTube search failed (${response.status}).`;
        try {
            const errorBody = await response.json();
            message = errorBody?.error?.message || message;
        } catch {
            // keep default message
        }
        throw new Error(message);
    }

    const data = await response.json();
    const items = Array.isArray(data?.items) ? data.items : [];

    const videos: YouTubeVideo[] = items
        .map((item: any): YouTubeVideo | null => {
            const videoId = item?.id?.videoId;
            const snippet = item?.snippet ?? {};
            if (!videoId) {
                return null;
            }
            const thumbnails = snippet.thumbnails ?? {};
            const thumb = thumbnails.medium ?? thumbnails.high ?? thumbnails.default ?? {};
            return {
                videoId,
                title: snippet.title ?? "",
                channelTitle: snippet.channelTitle ?? "",
                thumbnail: thumb.url ?? "",
                publishedAt: snippet.publishedAt ?? "",
            };
        })
        .filter((video: YouTubeVideo | null): video is YouTubeVideo => video != null);

    const page: YouTubeSearchPage = {
        videos,
        nextPageToken: data.nextPageToken,
        prevPageToken: data.prevPageToken,
    };
    resultCache.set(cacheKey, page);
    return page;
}
